import { Button, EmptyState, Icon } from "@iec-id/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { AuthCard, AuthPending } from "@/components/signin/auth-card";
import { ContinueStep } from "@/components/signin/continue-step";
import { useSigninT } from "@/components/signin/signin-i18n";
import {
	fallbackDestination,
	needsAccountChooser,
	parseAuthSearch,
	resolveService,
} from "@/lib/auth-flow";
import { clearHawkUser, setHawkUser } from "@/lib/hawk";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/continue")({
	validateSearch: parseAuthSearch,
	component: ContinueScreen,
});

/**
 * «Продолжить в {сервис}» (макет 4a, карточка 3).
 *
 * Ходит на `auth.sessionHint` сам, а не принимает данные от `/`: на этот адрес
 * человек может прийти по ссылке из сервиса напрямую, и экран обязан работать
 * без предыдущего шага. Сессии нет — возвращаемся к форме входа.
 */
function ContinueScreen() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const t = useSigninT();
	const service = resolveService(search);

	const hint = api.auth.sessionHint.useQuery(service ? { serviceId: service.id } : {}, {
		retry: false,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});
	const me = api.auth.me.useQuery(undefined, {
		retry: false,
		enabled: hint.data?.state === "session",
	});

	useEffect(() => {
		if (me.data?.userId) setHawkUser(me.data.userId);
		else clearHawkUser();
	}, [me.data?.userId]);

	const noSession = hint.isError || hint.data?.state === "none";
	const firstParty = !needsAccountChooser(search);

	useEffect(() => {
		if (noSession) void navigate({ to: "/", search, replace: true });
	}, [noSession, navigate, search]);

	useEffect(() => {
		if (hint.data?.state !== "session" || !me.data) return;
		if (me.data.needsOnboarding) {
			void navigate({ to: "/onboarding", search, replace: true });
			return;
		}
		if (!firstParty) return;
		window.location.assign(fallbackDestination(search));
	}, [firstParty, hint.data?.state, me.data, navigate, search]);

	// Сессия есть, а `auth.me` упал (сеть, 500): раньше экран крутил спиннер
	// вечно — `me.data` не появлялся, и условие ниже не разрешалось никогда.
	// Тупик без кнопки хуже ошибки: человек видит причину и может повторить.
	if (hint.data?.state === "session" && me.isError) {
		return (
			<AuthCard>
				<EmptyState
					icon={<Icon name="CircleAlert" size={32} />}
					title={t("continue.errorTitle")}
					description={t("continue.errorDescription")}
					action={
						<Button variant="second" onClick={() => void me.refetch()}>
							{t("common.retry")}
						</Button>
					}
				/>
			</AuthCard>
		);
	}

	if (hint.data?.state !== "session" || firstParty || me.data?.needsOnboarding !== false) {
		return <AuthPending label={t("continue.loading")} />;
	}

	return <ContinueStep search={search} hint={hint.data} />;
}
